import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import { IconButton } from "@/components/common/IconButton";
import { HITLSubmittingNote } from "@/components/hitl/HITLSubmittingNote";
import { useSubmitHITLToolResult } from "@/components/hitl/useSubmitHITLToolResult";
import { SECTION_HEADER_STYLE } from "@/lib/pane-styles";
import {
	environmentFileContentUrl,
	lookupEnvironmentFileByPath,
	type EnvironmentFileDto,
} from "@/lib/api";
import {
	clearSegmentationDraft,
	readSegmentationDraft,
	writeSegmentationDraft,
} from "@/lib/segmentation-draft-state";
import { selectEffectiveEnvironmentId, useChatStore } from "@/stores/useChatStore";
import { useToastStore } from "@/stores/useToastStore";
import { SegmentCard } from "./SegmentCard";
import { SegmentLabelSearchBar } from "./SegmentLabelSearchBar";
import { Timeline } from "./Timeline";
import { findLabelMatches, groupMatchesBySegment, type LabelMatch } from "./labelSearch";
import { createSegmentationStateFromSuggestedSegments } from "./suggestedSegments";
import {
	addBoundary,
	boundaryNudgeSeconds,
	computeSegments,
	createSegmentationState,
	findBoundaryInsertionIndex,
	moveBoundary,
	removeBoundary,
	replaceAllLabelMatches,
	replaceLabelMatch,
	setLabel,
	shiftSegmentIndexForInsert,
	shiftSegmentIndexForRemove,
	toSegmentJSON,
	type SegmentationState,
} from "./useSegmentation";
import { VideoPlayer } from "./VideoPlayer";

/** セグメントに一致がない場合の共有インスタンス。毎レンダー新規配列を作ると SegmentCard の memo が効かない。 */
const EMPTY_MATCHES: readonly LabelMatch[] = [];

/** 動画の読み込み前に Timeline へ渡す共有インスタンス (Track の memo を効かせる)。 */
const EMPTY_BOUNDARIES: readonly number[] = [];

/** 下書きの sessionStorage 書き込みを遅らせる猶予 (ミリ秒)。連続する打鍵の都度書き込まないようにする。 */
const DRAFT_WRITE_DEBOUNCE_MS = 200;

export function SegmentationPanel() {
	const hitlState = useChatStore((s) => s.hitlState);
	const chatId = useChatStore((s) => s.chatId);
	const environmentId = useChatStore(selectEffectiveEnvironmentId);
	const clearHITL = useChatStore((s) => s.clearHITL);
	const updateHITLSegmentation = useChatStore((s) => s.updateHITLSegmentation);
	const { submit: submitToolResult, submitting } = useSubmitHITLToolResult();

	const active = hitlState?.kind === "segmentation";
	const toolUseId = active ? hitlState.toolUseId : "";
	const videoPath = active ? hitlState.videoPath : "";
	const initialBoundaries = active ? hitlState.boundaries : undefined;
	const initialLabels = active ? hitlState.labels : undefined;
	const suggestedSegments = active ? hitlState.suggestedSegments : undefined;

	const [state, setState] = useState<SegmentationState | null>(null);
	const [selectedBoundary, setSelectedBoundary] = useState<number | null>(null);
	const [selectedSegment, setSelectedSegment] = useState<number | null>(null);
	const [hoveredSegment, setHoveredSegment] = useState<number | null>(null);
	const [currentTime, setCurrentTime] = useState(0);
	const [seekTo, setSeekTo] = useState<{ time: number } | null>(null);
	const [isDragging, setIsDragging] = useState(false);
	const [expandedSegments, setExpandedSegments] = useState<Set<number>>(() => new Set());
	const [searchOpen, setSearchOpen] = useState(false);
	const [searchQuery, setSearchQuery] = useState("");
	const [replacement, setReplacement] = useState("");
	const [caseSensitive, setCaseSensitive] = useState(false);
	const [activeMatchIndex, setActiveMatchIndex] = useState(0);
	const timelineRef = useRef<HTMLDivElement>(null);
	const segmentRefs = useRef(new Map<number, HTMLDivElement>());
	const panelRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		setState(null);
		setSelectedBoundary(null);
		setSelectedSegment(null);
		setHoveredSegment(null);
		setCurrentTime(0);
		setSeekTo(null);
		setIsDragging(false);
		setExpandedSegments(new Set());
		setSearchOpen(false);
		setActiveMatchIndex(0);
		segmentRefs.current.clear();
	}, [videoPath]);

	useEffect(() => {
		if (!state || isDragging || !toolUseId) return;
		updateHITLSegmentation(state.boundaries, state.labels);

		// sessionStorage への書き込みは打鍵のたびに同期実行すると重いので、直近の値だけを遅延反映する。
		const boundaries = [...state.boundaries];
		const labels = [...state.labels];
		const timer = setTimeout(() => {
			writeSegmentationDraft(toolUseId, { boundaries, labels });
		}, DRAFT_WRITE_DEBOUNCE_MS);
		return () => clearTimeout(timer);
	}, [state, isDragging, toolUseId, updateHITLSegmentation]);

	// 動画はツリーの読み込み状態に依存せずサーバーへ問い合わせて解決する。解決前の環境・パスに
	// 対する結果を使わないよう、解決時点の環境とパスも一緒に持つ。
	const [resolvedVideo, setResolvedVideo] = useState<{
		environmentId: string;
		path: string;
		file: EnvironmentFileDto;
	} | null>(null);

	useEffect(() => {
		if (!videoPath || !environmentId) return;
		let cancelled = false;
		lookupEnvironmentFileByPath(environmentId, videoPath)
			.then((file) => {
				if (cancelled || !file) return;
				setResolvedVideo({ environmentId, path: videoPath, file });
			})
			.catch((err: unknown) => {
				if (cancelled) return;
				console.error("segmentation video lookup failed", err);
				useToastStore
					.getState()
					.show({ type: "error", message: "動画ファイルを読み込めませんでした。" });
			});
		return () => {
			cancelled = true;
		};
	}, [environmentId, videoPath]);

	// 動画本体は backend がサンドボックスから直接ストリーミングするため、URL は決定的に組み立てられる。
	// updatedAt を version に乗せて内容更新時にキャッシュを無効化する。
	const videoFile = useMemo(() => {
		if (!videoPath) return undefined;
		return resolvedVideo?.path === videoPath && resolvedVideo.environmentId === environmentId
			? resolvedVideo.file
			: undefined;
	}, [environmentId, videoPath, resolvedVideo]);
	const videoUrl = useMemo(
		() =>
			environmentId && videoFile
				? environmentFileContentUrl(environmentId, videoFile.id, { version: videoFile.updatedAt })
				: "",
		[environmentId, videoFile],
	);

	const handleDurationLoaded = useCallback(
		(duration: number) => {
			const hasInitialState = Boolean(initialBoundaries?.length) || Boolean(initialLabels?.length);
			// ストアに編集内容が残っていなければ (=リロード直後)、同じタブ内に保存済みの
			// 編集中データがあればそれを優先する。無ければ ML の提案セグメントへ倒す。
			const draft = hasInitialState || !toolUseId ? null : readSegmentationDraft(toolUseId);
			let nextState =
				hasInitialState || draft
					? createSegmentationState(videoPath, duration)
					: createSegmentationStateFromSuggestedSegments(
							videoPath,
							duration,
							suggestedSegments ?? [],
						);
			if (initialBoundaries?.length)
				nextState = { ...nextState, boundaries: [...initialBoundaries] };
			if (initialLabels?.length) nextState = { ...nextState, labels: [...initialLabels] };
			if (!hasInitialState && draft) {
				nextState = { ...nextState, boundaries: [...draft.boundaries], labels: [...draft.labels] };
			}
			setState(nextState);
		},
		// 動画の読み込み時に一度だけ初期値を反映する。
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[videoPath],
	);

	const handleAddBoundary = useCallback(
		(time: number) => {
			if (!state) return;
			const nextState = addBoundary(state, time);
			if (nextState === state) return;
			const clamped = Math.max(0, Math.min(time, state.duration));
			const insertionIndex = findBoundaryInsertionIndex(state.boundaries, clamped);
			setState(nextState);
			setSelectedSegment((current) =>
				current === null ? null : shiftSegmentIndexForInsert(current, insertionIndex),
			);
			setHoveredSegment((current) =>
				current === null ? null : shiftSegmentIndexForInsert(current, insertionIndex),
			);
			setExpandedSegments(
				(current) =>
					new Set(Array.from(current, (i) => shiftSegmentIndexForInsert(i, insertionIndex))),
			);
		},
		[state],
	);

	const handleMoveBoundary = useCallback(
		(index: number, time: number) => {
			if (!state) return;
			const nextState = moveBoundary(state, index, time);
			setState(nextState);
			setSeekTo({ time: nextState.boundaries.at(index) ?? time });
		},
		[state],
	);

	const handleRemoveBoundary = useCallback((index: number) => {
		setState((current) => (current ? removeBoundary(current, index) : current));
		setSelectedBoundary(null);
		setSelectedSegment((current) =>
			current === null ? null : shiftSegmentIndexForRemove(current, index),
		);
		setHoveredSegment((current) =>
			current === null ? null : shiftSegmentIndexForRemove(current, index),
		);
		setExpandedSegments(
			(current) => new Set(Array.from(current, (i) => shiftSegmentIndexForRemove(i, index))),
		);
	}, []);

	const handleKeyDown = useCallback(
		(e: React.KeyboardEvent) => {
			if (e.nativeEvent.isComposing) return;
			if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "f") {
				e.preventDefault();
				setSearchOpen(true);
				return;
			}
			if (e.key === "Escape" && searchOpen) {
				e.preventDefault();
				setSearchOpen(false);
				return;
			}
			if (!state || selectedBoundary === null) return;
			const tag = (e.target as HTMLElement).tagName;
			if (tag === "INPUT" || tag === "TEXTAREA") return;
			if (e.key === "Delete" || e.key === "Backspace") {
				e.preventDefault();
				handleRemoveBoundary(selectedBoundary);
				return;
			}
			const nudge = boundaryNudgeSeconds(e.key, e.shiftKey);
			const time = state.boundaries.at(selectedBoundary);
			if (nudge !== null && time !== undefined) {
				e.preventDefault();
				handleMoveBoundary(selectedBoundary, time + nudge);
			}
		},
		[state, searchOpen, selectedBoundary, handleRemoveBoundary, handleMoveBoundary],
	);

	// マーカーの pointerdown は既定動作を止めるため、クリックしただけではフォーカスが移らない。
	// ←→ での微調整は器の onKeyDown が受け取るため、選択のたびにフォーカスを寄せる。
	const handleSelectBoundary = useCallback((index: number) => {
		setSelectedBoundary(index);
		panelRef.current?.focus({ preventScroll: true });
	}, []);

	const handleLabelChange = useCallback((index: number, label: string) => {
		setState((current) => (current ? setLabel(current, index, label) : current));
	}, []);

	const handleSeekSegment = useCallback((index: number, start: number) => {
		setSelectedSegment(index);
		setSeekTo({ time: start });
		segmentRefs.current.get(index)?.scrollIntoView({ block: "nearest" });
	}, []);

	const handleHoverSegment = useCallback((index: number | null) => {
		setHoveredSegment(index);
	}, []);

	const handleDragStart = useCallback(() => setIsDragging(true), []);
	const handleDragEnd = useCallback(() => setIsDragging(false), []);

	const handleToggleExpanded = useCallback((index: number) => {
		setExpandedSegments((current) => {
			const next = new Set(current);
			if (next.has(index)) next.delete(index);
			else next.add(index);
			return next;
		});
	}, []);

	const segments = useMemo(() => (state ? computeSegments(state) : []), [state]);
	const matches = useMemo(
		() => findLabelMatches(state?.labels ?? [], searchQuery, caseSensitive),
		[state?.labels, searchQuery, caseSensitive],
	);
	const matchesBySegment = useMemo(() => groupMatchesBySegment(matches), [matches]);
	const activeMatch = matches[activeMatchIndex] ?? null;

	useEffect(() => {
		setActiveMatchIndex(0);
	}, [searchQuery, caseSensitive]);

	useEffect(() => {
		setActiveMatchIndex((current) =>
			matches.length === 0 ? 0 : Math.min(current, matches.length - 1),
		);
	}, [matches.length]);

	useEffect(() => {
		const handlePointerDown = (event: PointerEvent) => {
			if (event.target instanceof Node && timelineRef.current?.contains(event.target)) return;
			setSelectedBoundary(null);
			setSelectedSegment(null);
		};
		document.addEventListener("pointerdown", handlePointerDown);
		return () => document.removeEventListener("pointerdown", handlePointerDown);
	}, []);

	const navigateMatch = useCallback(
		(direction: number) => {
			if (matches.length === 0) return;
			const nextIndex = (activeMatchIndex + direction + matches.length) % matches.length;
			const match = matches[nextIndex];
			if (!match) return;
			setActiveMatchIndex(nextIndex);
			setExpandedSegments((current) => {
				const next = new Set(current);
				next.add(match.segmentIndex);
				return next;
			});
			segmentRefs.current.get(match.segmentIndex)?.scrollIntoView({ block: "nearest" });
		},
		[activeMatchIndex, matches],
	);

	const handleReplace = useCallback(() => {
		if (!activeMatch) return;
		setState((current) =>
			current ? replaceLabelMatch(current, activeMatch, replacement) : current,
		);
	}, [activeMatch, replacement]);

	const handleReplaceAll = useCallback(() => {
		if (matches.length === 0) return;
		const count = matches.length;
		setState((current) =>
			current ? replaceAllLabelMatches(current, matches, replacement) : current,
		);
		useToastStore.getState().show({ type: "success", message: `${count}件を置換しました` });
	}, [matches, replacement]);

	const handleComplete = useCallback(async () => {
		if (!state || !chatId || !hitlState) return;
		if (!videoFile) {
			useToastStore.getState().show({
				type: "error",
				message: "動画ファイルがエージェント内に見つかりません。",
			});
			return;
		}
		const submitted = await submitToolResult(toSegmentJSON(state, videoFile.id));
		if (submitted) clearSegmentationDraft(toolUseId);
	}, [state, chatId, hitlState, videoFile, toolUseId, submitToolResult]);

	const handleCancel = useCallback(async () => {
		if (!chatId || !hitlState) {
			clearSegmentationDraft(toolUseId);
			clearHITL();
			return;
		}
		// セッションは requires_action 状態で次のメッセージを受け付けないので、
		// キャンセル時もツール結果を返してエージェントを進める必要がある。
		const submitted = await submitToolResult({ status: "cancelled" });
		if (submitted) clearSegmentationDraft(toolUseId);
	}, [chatId, hitlState, toolUseId, clearHITL, submitToolResult]);

	if (!active) {
		return (
			<div
				data-testid="segmentation-empty"
				style={{
					display: "flex",
					height: "100%",
					alignItems: "center",
					justifyContent: "center",
				}}
			>
				<p style={{ fontSize: "var(--text-caption)", color: "var(--fg-3)" }}>
					セグメンテーションのリクエストはありません
				</p>
			</div>
		);
	}

	const hasSegments = segments.length > 0;
	const completeDisabled = !hasSegments || submitting;

	// 下部ペインは横長のため、動画とタイムラインを左、工程一覧を右に並べる。
	return (
		<div ref={panelRef} className="h-full min-h-0" onKeyDown={handleKeyDown} tabIndex={0}>
			<PanelGroup direction="horizontal">
				<Panel defaultSize={45} minSize={25}>
					<div className="flex h-full min-h-0 flex-col px-3 pt-2">
						<VideoPlayer
							src={videoUrl}
							seekTo={seekTo}
							onDurationLoaded={handleDurationLoaded}
							onTimeUpdate={setCurrentTime}
						/>

						{/* 長さが判明する前も空のバーを置いて高さを確保し、読み込み完了時に動画が縮むのを防ぐ。 */}
						{videoUrl !== "" && (
							<Timeline
								containerRef={timelineRef}
								duration={state?.duration ?? 0}
								currentTime={currentTime}
								boundaries={state?.boundaries ?? EMPTY_BOUNDARIES}
								segments={segments}
								selectedBoundary={selectedBoundary}
								selectedSegment={selectedSegment}
								hoveredSegment={hoveredSegment}
								onAddBoundary={handleAddBoundary}
								onMoveBoundary={handleMoveBoundary}
								onRemoveBoundary={handleRemoveBoundary}
								onSelectBoundary={handleSelectBoundary}
								onSelectSegment={handleSeekSegment}
								onHoverSegment={handleHoverSegment}
								onDragStart={handleDragStart}
								onDragEnd={handleDragEnd}
							/>
						)}
					</div>
				</Panel>
				<PanelResizeHandle className="izanagi-resize-handle" />
				<Panel defaultSize={55} minSize={30}>
					<div className="flex h-full min-h-0 flex-col">
						<div className="shrink-0 px-2 pt-1">
							<div style={{ ...SECTION_HEADER_STYLE, padding: "4px 12px 2px", minHeight: 28 }}>
								<span>工程一覧</span>
								<IconButton
									icon={searchOpen ? "x" : "search"}
									iconSize={13}
									size={22}
									onClick={() => setSearchOpen((open) => !open)}
									data-tooltip={searchOpen ? "検索を閉じる" : "ラベルを検索"}
									data-testid="segment-label-search-toggle"
								/>
							</div>

							{searchOpen && (
								<SegmentLabelSearchBar
									query={searchQuery}
									caseSensitive={caseSensitive}
									replacement={replacement}
									matches={matches}
									activeMatchIndex={activeMatchIndex}
									onQueryChange={setSearchQuery}
									onReplacementChange={setReplacement}
									onToggleCaseSensitive={() => setCaseSensitive((current) => !current)}
									onPrevious={() => navigateMatch(-1)}
									onNext={() => navigateMatch(1)}
									onReplace={handleReplace}
									onReplaceAll={handleReplaceAll}
									onClose={() => setSearchOpen(false)}
								/>
							)}
						</div>

						<div className="min-h-0 flex-1 overflow-y-auto px-3 pb-2">
							{!state && (
								<p className="py-4 text-center text-caption text-fg-3">動画を読み込み中</p>
							)}
							{state && segments.length === 0 && (
								<p className="py-4 text-center text-caption text-fg-3">
									タイムラインをダブルクリックして境界線を追加
								</p>
							)}
							{segments.map((segment, index) => (
								<div
									key={index}
									ref={(element) => {
										if (element) segmentRefs.current.set(index, element);
										else segmentRefs.current.delete(index);
									}}
									style={{ scrollMarginBlock: 8 }}
								>
									<SegmentCard
										index={index}
										segment={segment}
										label={state?.labels[index] ?? ""}
										matches={matchesBySegment.get(index) ?? EMPTY_MATCHES}
										activeMatch={activeMatch?.segmentIndex === index ? activeMatch : null}
										expanded={expandedSegments.has(index)}
										selected={selectedSegment === index}
										hovered={hoveredSegment === index}
										onLabelChange={handleLabelChange}
										onToggleExpanded={handleToggleExpanded}
										onSeek={handleSeekSegment}
										onHover={handleHoverSegment}
									/>
								</div>
							))}
						</div>

						<div className="flex shrink-0 items-center justify-start gap-2 border-t border-line-1 bg-surface-1 px-3 py-2">
							<button
								onClick={handleCancel}
								disabled={submitting}
								className="rounded-4 border border-line-1 px-3 py-1.5 text-caption text-fg-2 enabled:cursor-pointer enabled:hover:bg-surface-2 disabled:opacity-40"
								data-testid="cancel-segmentation"
							>
								キャンセル
							</button>
							<button
								onClick={handleComplete}
								disabled={completeDisabled}
								className={`rounded-4 bg-neutral-solid px-3 py-1.5 text-caption font-medium text-on-neutral-solid hover:bg-neutral-solid-hover disabled:opacity-40 ${completeDisabled ? "" : "cursor-pointer"}`}
								data-testid="complete-segmentation"
							>
								決定
							</button>
							{submitting && <HITLSubmittingNote />}
						</div>
					</div>
				</Panel>
			</PanelGroup>
		</div>
	);
}
